/**
 * Destino: seleção de funil/etapa e lista das etapas escolhidas.
 */
import React from 'react';
import Select from '../../../../components/ui/Select';
import { Button } from '../../../../components/ui/Button';
import { TrashIcon } from '../../../../components/icons';
import { useFollowUpDestinationPicker } from '../hooks/useFollowUpDestinationPicker';

interface FollowUpSendToFieldsProps {
    fieldId: string;
    stageIds: string[];
    onAddStage: (stageId: string) => void;
    onRemoveStage: (stageId: string) => void;
}

export default function FollowUpSendToFields({
    fieldId,
    stageIds,
    onAddStage,
    onRemoveStage,
}: FollowUpSendToFieldsProps) {
    const {
        funnels, pickerFunnelId, pickerStageId, pickerStages, selectedRows,
        handleFunnelChange, handleStageChange,
    } = useFollowUpDestinationPicker(stageIds, onAddStage);

    return (
        <section className="flex flex-col gap-s2" aria-labelledby={`${fieldId}-send-to`}>
            <h4
                id={`${fieldId}-send-to`}
                className="text-xs font-semibold uppercase tracking-wide text-muted"
            >
                Enviar para
            </h4>
            <p className="text-sm text-muted">
                {selectedRows.length === 0
                    ? 'Nenhuma etapa selecionada: o follow-up dispara em todas as etapas abertas.'
                    : `${selectedRows.length} ${selectedRows.length === 1 ? 'etapa selecionada' : 'etapas selecionadas'}.`}
            </p>

            <div className="grid grid-cols-1 gap-s2 sm:grid-cols-2">
                <Select
                    label="Funil"
                    value={pickerFunnelId}
                    onChange={(event) => handleFunnelChange(event.target.value)}
                    placeholder={funnels.length === 0 ? 'Nenhum funil' : 'Selecione o funil'}
                    disabled={funnels.length === 0}
                    options={funnels.map((funnel) => ({ value: funnel.id, label: funnel.name }))}
                    aria-label="Funil"
                />
                <Select
                    label="Etapa"
                    value={pickerStageId}
                    onChange={(event) => handleStageChange(event.target.value)}
                    placeholder={
                        !pickerFunnelId
                            ? 'Selecione um funil'
                            : pickerStages.length === 0
                              ? 'Nenhuma etapa'
                              : 'Selecione a etapa'
                    }
                    disabled={!pickerFunnelId || pickerStages.length === 0}
                    options={pickerStages
                        .filter((stage) => !stageIds.includes(stage.id))
                        .map((stage) => ({ value: stage.id, label: stage.name }))}
                    aria-label="Etapa do funil"
                />
            </div>

            {selectedRows.length > 0 && (
                <ul className="flex flex-col gap-s2">
                    {selectedRows.map((row) => (
                        <li
                            key={row.id}
                            className="flex items-center justify-between gap-s3 rounded-xl border border-border bg-background px-s3 py-s2"
                        >
                            <span className="flex min-w-0 items-center gap-s2">
                                <span
                                    className="size-2.5 shrink-0 rounded-full"
                                    style={{ backgroundColor: row.color || 'var(--color-primary)' }}
                                    aria-hidden
                                />
                                <span className="min-w-0 truncate text-sm text-default">
                                    {row.funnelName}
                                    <span className="text-muted"> · </span>
                                    {row.stageName}
                                </span>
                            </span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => onRemoveStage(row.id)}
                                aria-label={`Remover ${row.stageName}`}
                                className="min-h-11 min-w-11 touch-manipulation text-error hover:text-error md:min-h-8 md:min-w-8"
                            >
                                <TrashIcon className="size-4" aria-hidden="true" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
